import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AuthLayout } from '@/layouts/AuthLayout';
import { useAuth } from '@/auth/useAuth';
import { LoginError } from '@/auth/AuthContext';
import { api } from '@/api';

const schema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});
type FormValues = z.infer<typeof schema>;

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showRecover, setShowRecover] = useState(false);

  // Password recovery (email a reset link).
  const [recoverAccount, setRecoverAccount] = useState('');
  const [recoverBusy, setRecoverBusy] = useState(false);
  const [recoverSent, setRecoverSent] = useState(false);

  const onRecover = async () => {
    if (recoverBusy) return;
    setRecoverBusy(true);
    try {
      await api.recover(recoverAccount.trim());
      // Neutral confirmation regardless of whether the account exists.
      setRecoverSent(true);
    } finally {
      setRecoverBusy(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await login(values.username, values.password);
      navigate('/', { replace: true });
    } catch (err) {
      setServerError(err instanceof LoginError ? err.message : 'Unable to sign in. Please try again.');
    }
  };

  return (
    <AuthLayout>
      <form className="ast-login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-floating">
          <input
            className="form-control sans-serif ast-input-top"
            id="login-username"
            type="text"
            placeholder="Username"
            autoComplete="username"
            {...register('username')}
          />
          <label htmlFor="login-username">Username</label>
        </div>
        <div className="form-floating">
          <input
            className="form-control sans-serif ast-input-bottom"
            id="login-password"
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            {...register('password')}
          />
          <label htmlFor="login-password">Password</label>
        </div>

        <div className="ast-login-error sans-serif">
          {errors.username?.message || errors.password?.message || serverError || ' '}
        </div>

        <div className="row g-2 mb-2">
          <div className="col">
            <button
              type="button"
              className="btn btn-ast-blue function-button sans-serif w-100"
              onClick={() => setShowRecover((v) => !v)}
            >
              Recover
            </button>
          </div>
          <div className="col">
            <button
              type="submit"
              className="btn btn-ast-blue function-button sans-serif w-100"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing In…' : 'Sign In'}
            </button>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col">
            {/* Public — the rate calculator needs no sign-in. */}
            <Link to="/rate" className="btn btn-ast-blue function-button sans-serif w-100">
              Rate Calculator
            </Link>
          </div>
        </div>

        {showRecover && (
          <div className="ast-recover sans-serif">
            {recoverSent ? (
              <div className="text-center">
                If an account matches that username or email, a password reset link
                has been sent. Check your inbox.
              </div>
            ) : (
              <>
                <div className="text-center mb-2">
                  Enter your username or email and we'll send a reset link.
                </div>
                <input
                  className="form-control form-control-sm sans-serif mb-2"
                  type="text"
                  placeholder="Username or email"
                  autoComplete="username"
                  value={recoverAccount}
                  onChange={(e) => setRecoverAccount(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-ast-blue function-button sans-serif w-100"
                  disabled={recoverBusy || recoverAccount.trim() === ''}
                  onClick={() => void onRecover()}
                >
                  {recoverBusy ? 'Sending…' : 'Send Reset Link'}
                </button>
              </>
            )}
          </div>
        )}

        {(import.meta.env.VITE_USE_MOCK === '1' || import.meta.env.VITE_USE_MOCK === 'true') && (
          <div className="ast-login-hint sans-serif text-center mt-3">
            Mock mode — try <code>admin</code> or <code>user</code> with password <code>demo</code>.
          </div>
        )}
      </form>
    </AuthLayout>
  );
}
